<script>
	import {user, game, view, change_attribute, send_data} from "../client.svelte.js";
	import { protoSkills, protoDomains, protoActions, categories, bonusToString, signed} from "../core.js";
	import Sheet from "./Sheet.svelte";
	import SkillSelectDialog from "../dialogs/SkillSelectDialog.svelte";
	import Sidebar from "../elements/Sidebar.svelte";
	import {note, check} from "./SheetFields.svelte";

	let { disabled = false } = $props()
	let selFilter = $state(null)
	let addedSkills = $state([])		// skills added via the skill dialog (not yet proficiencies)

	const filters = [{label: "Allgem.", cat: 'G'}, {label: "Kampf", cat: 'C'}, {label: "Magie", cat: 'M'}]
	const orderedCats = ['GP', 'GI', 'GS', 'GN', 'GU', 'GK', 'GH', 'GC', 'G_', 'C', 'CS', 'M', 'MS']
	let filteredCats = $derived(
		(selFilter === null) ? orderedCats : orderedCats.filter(cat => cat[0] === selFilter))
	// $inspect(`ABILSSHEET - filteredCats`, filteredCats)

	const magProfic = user.proficiencies.get(`${view.character.ID}:mag`)
	let magicRealm = $derived(magProfic ? magProfic.comment.split("-")?.[0] ?? "Mixed" : null)
	let spentXP  = $derived(view.character.expendedXP + ((view.character.upgrading) ? view.character.reservedXP : 0))
	let usableXP = $derived(view.character.experience - view.character.expendedXP)


	const xpProgression = {Kampf: {base: 6.0, double: 5.0}, Magie: {base: 3.5, double: 5.0}, skills: {base: 3.0, double: 6.0}}
	function incremental_cost(skillID, newval) {
		let skill = protoSkills[skillID] ?? {category: 'skills', cost: 100}
		const xpProgr = xpProgression[skill.category] ?? xpProgression['skills']
		const factor = Math.pow(2,newval / xpProgr.double) + (newval === 1)   // first learned point has additional cost
		const cost = xpProgr.base * skill.cost / 100 * factor
		return Math.round(cost) }


	// Flat map of all proficiencies for lookups (tier filtering, domain rows)
	let allProfics = $derived(
		Object.fromEntries(
			Object.values(protoSkills)
				.filter(skill => skill.ID in view.character.profics || addedSkills.includes(skill.ID) || skill.inherent || (skill.cost >= 50 && skill.cat[0] === 'G'))
				.map(skill => {
					const profic = user.proficiencies.get(`${view.character.ID}:${skill.ID}`)
					const reqTraits = skill.traits.concat(skill.traits[0])
					const traitVals = reqTraits.map(traitID => user.traits.get(view.character.traits[traitID])?.value ?? 0)
					const bonus = Math.round(Math.sumPrecise(traitVals) / reqTraits.length)
					// const bonus = Math.round(skill.traits.reduce(
					// 	(accum, traitID) => accum + (user.traits.get(view.character.traits[traitID])?.value ?? 0),
					// 	(skill.traits.length > 0) ? user.traits.get(view.character.traits[skill.traits[0]])?.value ?? 0 : 0) / (skill.traits.length + 1) )
					const proficData = Object.assign({
						TYPE: 'Proficiency', CHAR: view.character.ID, SKILL: skill.ID, skillID: skill.ID,
						name: skill.name, cat: skill.cat, category: (skill.cost < 50) ? "Nebenfähigkeiten" : skill.category, includes: skill.includes,
						learned: null, increase: 0, bonus: bonus, value: (skill.inherent) ? bonus : null, expended: 0,
						details: `Eigenschaften [${skill.traits.join(", ")}] ${bonusToString(bonus)}`,
						incremXP: (profic) ? profic.incremXP : incremental_cost(skill.ID, 1)
					}, profic)
					if (view.character.upgrading && profic)
						Object.assign(proficData, {
							value: profic.value + profic.increase,
							expended: profic.expended + profic.reserved,
						})
					return proficData
				})
				.map(profic => [profic.SKILL, profic])	)	)
	// $inspect(`ABILSSHEET - allProfics`, allProfics)


	// Learned domains (combat + magic)
	let existDomains = $derived(
		Object.fromEntries(
			Object.keys(view.character.profics)
				.concat(addedSkills)
				.map(domID => protoDomains[domID])
				.filter(dom => dom && (!selFilter || dom.cat[0] === selFilter))
				.map(dom => [dom.ID, dom]) 	)  )
	// $inspect(`ABILSSHEET - existDomains`, existDomains)


	// All skills grouped by category — domain skills excluded (shown in domain section below)
	let groupedSkills = $derived(
		Object.assign(
			Object.groupBy(
				Object.values(protoSkills)
					.filter(skill => skill.inherent || skill.ID in allProfics)
					.filter(skill => skill.cat !== 'MS' || magProfic)
					.map(skill => allProfics[skill.ID] ?? skill)
					.sort((a, b) => a.name.localeCompare(b.name))
				, ({ cat }) => cat),
			{C: ['cmb'].map(capabID => allProfics[capabID]).concat(Object.values(allProfics).filter(profic => ['CR','CW'].includes(profic.cat))),
			 M: ['mag'].map(capabID => allProfics[capabID]).concat(Object.values(allProfics).filter(profic => ['MA','ME', 'MM', 'MV'].includes(profic.cat))) })  )
	// $inspect(`ABILSSHEET - groupedSkills`, groupedSkills)


	let availAbilities = $derived(
		Object.groupBy(
			Object.values(protoActions)
				.filter(action => action.TYPE === 'Ability' && action.domain in existDomains)
				.filter(abil => magProfic || abil.skill in existDomains)
				.map(abil => Object.assign(
					{ domain: abil.domain, name: abil.name, kind: abil.kind,
						learned: abil.level <= allProfics[abil.domain].value,  // for magic laymans this depends on their skill level
						TYPE: 'Mastery', CHAR: view.character.ID, ABIL: abil.name},
					user.masteries.get(`${view.character.ID};${abil.name}`)))
				.map(talent => (view.character.upgrading && talent.increase) ? Object.assign(talent, { learned: talent.increase > 0 }) : talent)
			, ({ domain }) => domain)  )

	let availPowers = $derived(
		Object.groupBy(
			Object.values(protoActions)
				.filter(action => action.TYPE === 'Power' && action.domain in existDomains)
				.filter(power => protoDomains[power.domain].cat[0] !== 'M' || magProfic || power.skill in existDomains)
				.map(action => Object.assign({
					domain: action.domain, name: action.name, tier: action.tier, kind: action.kind, cost: action.exertion, skill: action.skill,
					TYPE: 'Mastery', CHAR: view.character.ID, ABIL: action.name,
					available: (action.tier <= Math.ceil(((allProfics[action.domain]?.value ?? -1) + 1) / 5)),
				}, user.masteries.get(`${view.character.ID};${action.name}`)))
				.map(power => (view.character.upgrading && power.increase) ? Object.assign(power, { learned: power.increase > 0, expended: power.expended + power.reserved }) : power)
			, ({ domain }) => domain)  )
	// $inspect(`ABILSSHEET - availPowers`, availPowers)

	let existPowerXP = $derived(
		Object.fromEntries(
			Object.entries(availPowers)
				.map(([domID, powers]) => [domID, powers.reduce((agg, pow) => agg + (pow.expended ?? 0), 0)])
		)
	)

	/// skills not yet in allProfics and domains not yet in existDomains (offered in the skill dialog)
	let hasMagicDomain = $derived(Object.values(existDomains).some(dom => dom.cat[0] === 'M'))
	let newSkills = $derived(
		Object.values(protoSkills)
			.filter(skill => !(skill.ID in protoDomains) && !(skill.ID in allProfics) && filteredCats.includes(skill.cat))
			.filter(skill => skill.cat !== 'MS' || magProfic)
			.concat(Object.values(protoDomains)
				.filter(domain => !(domain.ID in existDomains || ["Kreatur", "Magie"].includes(domain.category) || domain.ID === "base"))
				.filter(domain => !selFilter || domain.cat[0] === selFilter)
				.filter(domain => domain.cat[0] !== 'M' || magProfic || !hasMagicDomain)
				.map(domain => Object.assign({}, protoSkills[domain.ID] ?? domain, {isDomain: true})))  )
	// $inspect(`ABILSSHEET - newSkills`, newSkills)

	/// category order in the skill dialog: skill categories first, then domain categories
	let dialogCats = $derived(
		filteredCats.concat(Object.keys(categories).filter(cat => !orderedCats.includes(cat) && (!selFilter || cat[0] === selFilter))) )


	const romans = ['', 'I', 'II', 'III', 'IV', 'V', 'VI']

	/** switches to upgrading and back again **/
	function toggle_upgrading(e) {
		const method = (view.character.upgrading) ? 'PUT' : 'POST'
		send_data('/upgrade', {character: view.character.ID}, method)
	}

	function reset_levelup(e) {
		send_data('/upgrade', {character: view.character.ID}, 'DELETE')
	}


	/** raises or lowers the increase of a proficiency by one step (only while upgrading) **/
	function step_decrease() {
		change_attribute.bind(this)({target: {id: 'increase', value: this.increase - 1}})
	}

	function step_increase() {
		change_attribute.bind(this)({target: {id: 'increase', value: this.increase + 1}})
	}

	/** adds a skill selected in the skill dialog to allProfics, or a domain to existDomains **/
	function add_skill(skillID) {
		if (!addedSkills.includes(skillID))
			addedSkills.push(skillID)
	}
</script>



<!-------------------------------- HTML -------------------------------------->

{#snippet filterFlyout()}
	<menu class="filters">
		{#each filters as filter}
			<li><button class="filter" class:active={selFilter === filter.cat}
			            onclick={() => selFilter = (selFilter === filter.cat) ? null : filter.cat}>
				{filter.label}
			</button></li>
		{/each}
	</menu>
{/snippet}

<section id='abils'>

	<Sidebar flyout={filterFlyout} items={[
		{id: 'upgrade', icon: 'arrow-up', primary: true, onclick: toggle_upgrading,
		 label: (view.character?.upgrading) ? `Abschließen ${usableXP - view.character.reservedXP} EP` : `Verbessern ${usableXP} EP`,
		 title: (view.character?.upgrading) ? "Verbesserung abschließen" : "Held mit Erfahrungspunkten verbessern"},
		{id: 'filter', icon: 'filter', label: "Filtern", flyout: "Fähigkeiten filtern", active: selFilter !== null} ]}/>

	<Sheet>
		{#snippet stat()}
			<div class="experience-box" data-help="experience">
				<label class="kicker">vergeben</label>
				<span class="num">{spentXP}</span>
				<label class="kicker">Gesamt EP</label>
				<span class="num">{view.character?.experience}</span>
			</div>
		{/snippet}

		{#each filteredCats as cat}
			{#if groupedSkills[cat]?.length > 0}
				<fieldset class="sheet-section" data-help="skills">
					<h2 class="sheet-title">{categories[cat]}{(['G','GP', 'GI', 'GS'].includes(cat)) ? " " : "-"}Fertigkeiten</h2>
					<div class="profics-table" class:upgrading={view.character.upgrading}>
						{@render skillrow()}
						{#each groupedSkills[cat] as profic}
							{@render skillrow(profic)}
						{/each}
					</div>
				</fieldset>
			{/if}
		{/each}

		{#if !disabled && view.character.upgrading && newSkills.length > 0}
			<button class="btn-outline add-skill" data-help="addskill" command="show-modal" commandfor="add-skill-dialog">
				<span class="fa icon-plus"></span>Fertigkeit hinzufügen
			</button>
		{/if}

		{#if Object.keys(existDomains).length > 0 || selFilter === 'C' || selFilter === 'M'}
			<section class="sheet-section" data-help="domains">
				<h2 class="sheet-title domains-title">
					{#if selFilter === 'M'}
						{#if magProfic}Gelernte Gebiete{:else}Besonderes Talent{/if}
					{:else}
						Spezialisierungen
					{/if}
				</h2>
				{#each Object.values(existDomains) as domain}
					{@render domaincard(domain, allProfics[domain.ID])}
				{:else}
					<p class="muted">Noch kein Gebiet gelernt.</p>
				{/each}
			</section>
		{/if}
	</Sheet>


	<SkillSelectDialog id='add-skill-dialog' skills={newSkills} cats={dialogCats} onadd={add_skill}/>


	{#snippet skillrow(profic)}
		{#if !profic}
			<div class="skill-row kicker colhead">
				<span data-help="skill">Fertigkeit</span>
				<span data-help="includes">beinhaltet</span>
				<span data-help="learned" class="center">gel.</span>
				{#if view.character.upgrading}<span data-help="increase" class="center">Steigern</span>{/if}
				<span data-help="bonus" class="center">Bonus</span>
				<span data-help="value" class="center">Wert</span>
				<span data-help="expended" class="right">Aufw</span>
				<span data-help="comment">Bemerkungen</span>
			</div>
		{:else if typeof profic === 'object' && profic}
			<div class="skill-row" class:upgraded={profic.increase > 0 || profic.increase < 0}>
				<a class="skill-name" data-help="skill" href="#{profic.SKILL}">{profic.name}</a>
				<span class="includes" data-help="includes">{profic.includes ?? ""}</span>
				<span class="center num" data-help="learned">{(profic.learned > 0) ? profic.learned : ""}</span>
				{#if view.character.upgrading}
					<!-- −/+ control for raising a value while upgrading, with the cost of the next step -->
					<span class="stepper" data-help="increase">
						<button class="step" disabled={disabled || !view.character.upgrading || profic.learned + profic.increase <= 0} onclick={step_decrease.bind(profic)}>−</button>
						<span class="step-incr num">{profic.increase ? `${signed(profic.increase)}` : "·"}</span>
						<button class="step" disabled={disabled || !view.character.upgrading} onclick={step_increase.bind(profic)}>+</button>
						{#if profic.incremXP !== undefined}<span class="step-cost num">({profic.incremXP} EP)</span>{/if}
					</span>
				{/if}
				<span class="center num bonus" data-help="bonus" title={profic.details}>{bonusToString(profic.bonus)}</span>
				<span class="center num total" data-help="value">{#if profic.value > 0}{profic.value}{:else}{@html '&nbsp;'}{/if}</span>
				<span class="right num spent" data-help="expended">{profic.expended} EP</span>
				{@render note({value: profic.comment, disabled: !profic.learned, placeholder: "", onchange: change_attribute.bind(profic)})}
			</div>
		{/if}
	{/snippet}

	{#snippet domaincard(domain, profic)}
		<div class="domain-card">
			<div class="domain-head">
				<h3 class="domain-name">{domain.name}</h3>
				<span class="spacer"></span>
				<span class="domain-stats">Aufwand</span>
				<span class="domain-stats num">{existPowerXP[domain.ID]} EP</span>
			</div>

			{#if domain.ID in availAbilities}
				<div class="abil-head">
					<span class="kicker">Aktionen</span>
				</div>
				<div class="chips" data-help="abilities">
					{#each availAbilities[domain.ID] as ability}
						{@render abilchip(ability)}
					{/each}
				</div>
			{/if}

			{#if domain.ID in availPowers}
				<div class="abil-head">
					<span class="kicker">Kräfte</span>
					<span class="hint">Aufw = {existPowerXP[domain.ID]} EP</span>
				</div>
				{#each Object.entries(Object.groupBy(availPowers[domain.ID] ?? [], ({tier}) => tier)) as [tier, rankPowers]}
					<div class="tier">
						<span class="tier-num">{romans[tier] ?? tier}</span>
						<div class="chips" data-help="powers">
							{#each rankPowers as power}
								{@render abilchip(power)}
							{/each}
						</div>
					</div>
				{/each}
			{/if}
		</div>
	{/snippet}

	{#snippet abilchip(abil)}
		{@const locked = disabled || !view.character.upgrading || abil.available === false || (abil.learned && !abil.increase)}   <!-- abilities learned before this level-up stay fixed; unavailable ones cannot be picked -->
		{@render check({label: abil.name, kind: abil.kind, checked: !!abil.learned, disabled: locked, dimmed: abil.available === false,
		                title: (abil.available === false) ? `Stufe in ${protoSkills[abil.skill]?.name} reicht noch nicht für diese Kraft` : "",
		                onchange: change_attribute.bind(abil)})}
	{/snippet}
</section>


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	/* filter flyout of the sidebar */
	.filters
		display: flex
		flex-direction: column
		gap: 8px
		margin: 8px 0

	.filter
		width: 100%
		padding: 6px 12px
		border: 1px solid colors.$grey-30
		border-radius: 4px
		background: colors.$grey-20
		color: colors.$grey-85
		cursor: pointer

		&:hover
			background: colors.$grey-30

		&.active
			border-color: colors.$primary-100
			background: colors.$primary-100
			color: colors.$white

	/* experience stats shown in sheet title  */
	.experience-box
		display: grid
		grid-template-columns: 10ch 6ch
		align-items: baseline
		grid-gap: 6px

		span
			font-family: var(--font-heading)
			font-size: 30px
			line-height: 1.1

		.num
			text-align: right


	/* skills table: one subgrid row per proficiency */
	.profics-table
		display: grid
		grid-template-columns: 15ch minmax(0, 1fr) 36px 48px 48px 64px minmax(0, 1fr)
		column-gap: 16px

		&.upgrading
			grid-template-columns: 15ch minmax(120px, 1fr) 36px 140px 48px 48px 64px minmax(120px, 1fr)

	.skill-row
		grid-column: 1 / -1
		display: grid
		grid-template-columns: subgrid
		align-items: center
		padding: 7px 0
		border-bottom: 1px solid var(--color-divider)

		&.colhead
			align-items: end
			padding: 8px 0
			border-bottom-color: var(--color-accent-300)
			letter-spacing: .12em

		&:not(.colhead):hover
			background: var(--hover-tint)

		&.upgraded
			background: color-mix(in srgb, var(--color-accent) 10%, transparent)

		:global(.note)
			padding: 0
			font-size: 14px

	.skill-name
		font-size: 16px

		&:hover
			text-decoration: underline var(--color-accent)

	.includes
		font-size: 13px
		font-style: italic
		color: var(--color-neutral-700)

	.bonus
		color: var(--color-neutral-700)

	.total
		padding-bottom: 2px
		border-bottom: 1px solid var(--color-accent)
		font-family: var(--font-heading)
		font-size: 22px
		line-height: 1

	.spent
		font-size: 12px
		color: var(--color-neutral-600)


	/* stepper */
	.stepper
		display: flex
		align-items: center
		justify-content: center
		gap: 6px

	.step
		width: 22px
		height: 22px
		padding: 0
		border: 1px solid var(--color-accent)
		border-radius: var(--radius-sm)
		background: transparent
		color: var(--color-accent-800)
		line-height: 1
		cursor: pointer

		&:hover:not(:disabled)
			background: var(--color-accent-200)

		&:disabled
			opacity: .45
			cursor: default

	.step-incr
		min-width: 22px
		text-align: center
		font-weight: 600
		color: var(--color-accent-800)

	.step-cost
		font-size: 11px
		color: var(--color-neutral-600)


	/* domains */
	.domains-title
		margin-bottom: 14px

	.domain-card
		margin-bottom: 18px
		padding: 18px 22px
		border: 1px solid var(--color-accent-300)
		border-radius: var(--radius-md)

	.domain-head
		display: flex
		align-items: baseline
		flex-wrap: wrap
		gap: 16px
		padding-bottom: 10px
		border-bottom: 1px solid var(--color-divider)

	.domain-name
		font-size: 24px

	.spacer
		flex: 1

	.domain-stats
		font-size: 13px
		color: var(--color-neutral-700)

		&.num
			font-size: 100%

	.domain-value
		padding: 0 6px 2px
		border-bottom: 1px solid var(--color-accent)
		font-family: var(--font-heading)
		font-size: 26px
		line-height: 1

	.abil-head
		display: flex
		align-items: baseline
		gap: 10px
		margin: 16px 0 8px

	.chips
		display: flex
		flex-wrap: wrap
		gap: 8px

	.tier
		display: grid
		grid-template-columns: 44px minmax(0, 1fr)
		align-items: center
		padding: 6px 0
		border-top: 1px solid var(--color-divider)

	.tier-num
		font-family: var(--font-heading)
		font-size: 22px
		color: var(--color-accent-700)

	.add-skill
		margin: 4px 0 24px
</style>
